import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Chip, 
  OutlinedInput, 
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CalendarToday, 
  LocationOn, 
  Business, 
  AttachMoney,
  ArrowForward,
  Event
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchHackathonStatusCounts } from '../services/analytics';
import { getAllHackathonsAdmin } from '../services/api';
import useDebounce from '../hooks/useDebounce';

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

const countryOptions = [
  { value: '', label: 'All Countries' },
  { value: 'India', label: 'India' },
  { value: 'International', label: 'International' },
];

const paidOptions = [
  { value: '', label: 'All' },
  { value: 'free', label: 'Free' },
  { value: 'paid', label: 'Paid' },
];

const createdDateOptions = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Created Today' },
  { value: '2days', label: 'Created in Last 2 Days' },
  { value: '7days', label: 'Created in Last 7 Days' },
];

const HackathonAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusCounts, setStatusCounts] = useState<any>({});
  const [filters, setFilters] = useState({
    status: [],
    country: '',
    state: '',
    paid: '',
    search: '',
    organization: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    dateRange: '',
    active: '',
    showExpired: false,
    createdDateFilter: '',
  });
  const [hackathons, setHackathons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    status: [],
    country: '',
    state: '',
    paid: '',
    startDate: '',
    endDate: '',
    registrationStartDate: '',
    registrationEndDate: '',
    dateRange: '',
    active: '',
    showExpired: false,
    createdDateFilter: '',
  });

  // Debounced search fields
  const debouncedSearch = useDebounce(filters.search, 400);
  const debouncedOrganization = useDebounce(filters.organization, 400);

  useEffect(() => {
    async function loadStatusCounts() {
      try {
        const counts = await fetchHackathonStatusCounts();
        setStatusCounts(counts);
      } catch (error) {
        console.error('Error loading status counts:', error);
      }
    }
    loadStatusCounts();
  }, []);

  useEffect(() => {
    async function loadHackathons() {
      setLoading(true);
      setError(null);
      try {
        const apiFilters = {
          search: debouncedSearch.trim(),
          organization: debouncedOrganization.trim(),
          status: appliedFilters.status.length > 0 ? appliedFilters.status.join(',') : undefined,
          country: appliedFilters.country,
          state: appliedFilters.state,
          registrationFees: appliedFilters.paid,
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          registrationStartDate: appliedFilters.registrationStartDate,
          registrationEndDate: appliedFilters.registrationEndDate,
          dateRange: appliedFilters.dateRange,
          active: appliedFilters.active,
          showExpired: appliedFilters.showExpired,
          createdDateFilter: appliedFilters.createdDateFilter,
        };
        const data = await getAllHackathonsAdmin(apiFilters);
        setHackathons(data.data || []);
        setLoading(false);
      } catch (error) {
        setError('Failed to load hackathons');
        setHackathons([]);
        setLoading(false);
      }
    }
    loadHackathons();
  }, [debouncedSearch, debouncedOrganization, appliedFilters]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleCardClick = (hackathon: any) => {
    navigate(`/hackathons/${hackathon._id}`);
  };

  return (
    <Box maxWidth={1200} mx="auto" p={2}>
      <Typography variant="h4" fontWeight={700} color="primary" mb={3}>
        Hackathon Analytics
      </Typography>
      
      {/* Summary Cards */}
      <Box display="flex" flexWrap="wrap" gap={3} mb={4}>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Total</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.total ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Pending</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.pending ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Accepted</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.accepted ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={3} sx={{ p: 3, flex: '1 1 180px', minWidth: 180 }}>
          <Typography variant="h6" fontWeight={600} mb={1}>Rejected</Typography>
          <Typography variant="h4" color="primary.main" fontWeight={800}>
            {statusCounts?.rejected ?? 0}
          </Typography>
        </Paper>
      </Box>
      
      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={2}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {statusOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={filters.country}
              onChange={e => handleFilterChange('country', e.target.value)}
              label="Country"
            >
              {countryOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="State"
            value={filters.state}
            onChange={e => handleFilterChange('state', e.target.value)}
            fullWidth
          />
          
          <FormControl fullWidth>
            <InputLabel>Free/Paid</InputLabel>
            <Select
              value={filters.paid}
              onChange={e => handleFilterChange('paid', e.target.value)}
              label="Free/Paid"
            >
              {paidOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Search by Name"
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            fullWidth
          />
          
          <TextField
            label="Search by Organization"
            value={filters.organization}
            onChange={e => handleFilterChange('organization', e.target.value)}
            fullWidth
          />
          
          <TextField
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={e => handleFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={e => handleFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="Registration Start Date"
            type="date"
            value={filters.registrationStartDate}
            onChange={e => handleFilterChange('registrationStartDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <TextField
            label="Registration End Date"
            type="date"
            value={filters.registrationEndDate}
            onChange={e => handleFilterChange('registrationEndDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          
          <FormControl fullWidth>
            <InputLabel>Show by Created Date</InputLabel>
            <Select
              value={filters.createdDateFilter}
              onChange={e => handleFilterChange('createdDateFilter', e.target.value)}
              label="Show by Created Date"
            >
              {createdDateOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <FormControl>
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                id="showExpired"
                checked={filters.showExpired}
                onChange={e => handleFilterChange('showExpired', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <label htmlFor="showExpired" style={{ cursor: 'pointer' }}>
                <Typography variant="body2">Show expired hackathons (based on registration end date)</Typography>
              </label>
            </Box>
          </FormControl>
        </Box>
        
        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <Button 
            variant="outlined" 
            onClick={() => setFilters({
              status: [],
              country: '',
              state: '',
              paid: '',
              search: '',
              organization: '',
              startDate: '',
              endDate: '',
              registrationStartDate: '',
              registrationEndDate: '',
              dateRange: '',
              active: '',
              showExpired: false,
              createdDateFilter: '',
            })}
          >
            Clear Filters
          </Button>
          <Button variant="contained" color="primary" onClick={handleApplyFilters}>
            Apply Filters
          </Button>
        </Box>
      </Paper>
      
      {/* Hackathons Cards */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          All Hackathons
        </Typography>
        
        {loading ? (
          <Typography>Loading hackathons...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            {!Array.isArray(hackathons) ? (
              <Typography>Invalid data format received</Typography>
            ) : hackathons.length === 0 ? (
              <Typography>No hackathons found.</Typography>
            ) : (
              <Box 
                display="grid" 
                gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" 
                gap={3}
              >
                {Array.isArray(hackathons) && hackathons.map((hackathon, index) => (
                  <Box key={hackathon._id || hackathon.id || index}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        }
                      }}
                      onClick={() => handleCardClick(hackathon)}
                    >
                      <Box sx={{ position: 'relative', height: 140, width: '100%' }}>
                        {hackathon.images?.banner || hackathon.banner ? (
                          <CardMedia
                            component="img"
                            height="140"
                            image={hackathon.images?.banner || hackathon.banner}
                            alt={hackathon.title || 'Hackathon'}
                            sx={{
                              objectFit: 'cover',
                              width: '100%',
                              height: '100%',
                              backgroundColor: 'grey.200',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = '';
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              minHeight: 140,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              background: 'linear-gradient(135deg, #5b7fff 0%, #6ec6ff 100%)',
                            }}
                          >
                            <Event sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                            <Typography variant="subtitle1" color="white" fontWeight={700} align="center" sx={{ px: 2, textShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                              {hackathon.title || 'Hackathon'}
                            </Typography>
                          </Box>
                        )}
                        {/* Featured Chip - top left, yellow */}
                        {hackathon.featured && (
                          <Chip
                            label="Featured"
                            color="warning"
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 10,
                              left: 10,
                              zIndex: 2,
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              backgroundColor: '#ffe066',
                              color: '#7a5c00',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            }}
                          />
                        )}
                        {/* Status Chip - top right, bold, white text, solid bg */}
                        <Chip
                          label={hackathon.status || 'N/A'}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            px: 1.5,
                            color: '#fff',
                            backgroundColor:
                              hackathon.status === 'accepted'
                                ? '#43a047'
                                : hackathon.status === 'pending'
                                ? '#fbc02d'
                                : hackathon.status === 'rejected'
                                ? '#e53935'
                                : '#607d8b',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                            textTransform: 'capitalize',
                            letterSpacing: 0.5,
                          }}
                        />
                      </Box>

                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        {/* Header */}
                        <Box mb={2}>
                          <Typography variant="h6" component="h3" fontWeight={600} sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2
                          }}>
                            {hackathon.title || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Organization */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <Business sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {hackathon.organization?.name || hackathon.organization || 'N/A'}
                          </Typography>
                        </Box>

                        {/* Location */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {hackathon.location?.country || hackathon.country || 'N/A'}
                            {hackathon.location?.state || hackathon.state ? 
                              `, ${hackathon.location?.state || hackathon.state}` : ''}
                          </Typography>
                        </Box>

                        {/* Dates */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {hackathon.dates?.startDate || hackathon.startDate ? 
                              new Date(hackathon.dates?.startDate || hackathon.startDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Box>

                        {/* Registration Fees */}
                        <Box display="flex" alignItems="center" mb={1.5}>
                          <AttachMoney sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            {hackathon.registrationFees === 0 ? 'Free' : `₹${hackathon.registrationFees}`}
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                          <Typography variant="caption" color="text.secondary">
                            Click to view details
                          </Typography>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary">
                              <ArrowForward />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HackathonAnalyticsPage; 